// =============================================================================
// NavDebugChip — on-demand layout diagnostic for the bottom nav vs the viewport.
// OFF by default and visible ONLY to the two allowed operators below — never to
// anyone else, even if the flag/gesture is set. Enable it either way:
//   • In a browser: visit `<origin>/?navdebug=1` (disable with `?navdebug=0`).
//   • In the installed PWA (standalone): **3-finger tap** anywhere to toggle —
//     iOS keeps the home-screen app's localStorage separate from Safari, so the
//     URL flag doesn't carry over; the gesture is the in-app switch.
// Shows live geometry (innerHeight, visualViewport, the fixed nav's rect + how far
// it sits from the viewport bottom = DRIFT, position/transform, body padding,
// display-mode). Kept as a permanent debugging aid — invisible unless toggled on
// by an allowed user.
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

const KEY = 'kf49.navdebug'
// Only these operators may see the diagnostic, even with the flag/gesture set.
const ALLOWED_EMAILS = new Set(['thomas@kf49citrus.com', 'jason@kf49citrus.com'])

// fallow-ignore-next-line complexity
function snapshot(): string {
  const nav = document.querySelector('nav[aria-label="Primary"]') as HTMLElement | null
  const r = nav?.getBoundingClientRect()
  const cs = nav ? getComputedStyle(nav) : null
  const n = (v: number | undefined) => (v == null ? -1 : Math.round(v))
  const ih = window.innerHeight
  const vv = window.visualViewport ? Math.round(window.visualViewport.height) : -1
  const lines = [
    `ih:${ih} vv:${vv} dcH:${document.documentElement.clientHeight}`,
    `screen:${window.screen.height} avail:${window.screen.availHeight} gap:${Math.round(window.screen.height - ih)}`,
    `navTop:${n(r?.top)} navBot:${n(r?.bottom)} DRIFT:${r ? Math.round(ih - r.bottom) : -1}`,
    `pos:${cs?.position ?? '-'} tf:${(cs?.transform ?? '-').slice(0, 24)}`,
    `padB:${getComputedStyle(document.body).paddingBottom} standalone:${window.matchMedia('(display-mode: standalone)').matches}`,
    `scrollH:${document.documentElement.scrollHeight} hasNav:${document.body.classList.contains('has-bottom-nav')}`,
  ]
  return lines.join('\n')
}

function flagOn(): boolean {
  try { return localStorage.getItem(KEY) === '1' } catch { return false }
}

export default function NavDebugChip() {
  const [on, setOn] = useState(false)
  const [allowed, setAllowed] = useState(false)
  const [text, setText] = useState('')

  // Gate to the allowed emails. The email lives on the auth session, not
  // useCurrentUser (which doesn't select it).
  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession()
      .then(({ data }) => {
        const email = (data.session?.user?.email ?? '').toLowerCase()
        if (!cancelled) setAllowed(ALLOWED_EMAILS.has(email))
      })
      .catch(() => { /* not signed in → stays disallowed */ })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('navdebug')
      if (p != null) localStorage.setItem(KEY, p === '0' ? '0' : '1')
    } catch { /* localStorage unavailable */ }
    setOn(flagOn())
  }, [])

  // 3-finger tap toggles the flag — the in-app switch for the installed PWA
  // (iOS keeps its localStorage separate from Safari, so the ?navdebug URL flag
  // doesn't carry over). 3 fingers can't collide with normal single-tap UI.
  useEffect(() => {
    function onTouch(e: TouchEvent) {
      if (e.touches.length !== 3) return
      const next = flagOn() ? '0' : '1'
      try { localStorage.setItem(KEY, next) } catch { /* */ }
      setOn(next === '1')
    }
    document.addEventListener('touchstart', onTouch, { passive: true })
    return () => document.removeEventListener('touchstart', onTouch)
  }, [])

  const show = on && allowed
  useEffect(() => {
    if (!show) return
    let raf = 0
    const tick = () => { setText(snapshot()); raf = requestAnimationFrame(tick) }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [show])

  if (!show) return null
  return (
    <pre
      style={{
        position: 'fixed', top: 'env(safe-area-inset-top)', left: 0, zIndex: 99999,
        margin: 0, padding: '6px 8px', maxWidth: '78vw',
        background: 'rgba(0,0,0,0.85)', color: '#39ff14',
        font: '11px/1.35 ui-monospace, monospace', pointerEvents: 'none', whiteSpace: 'pre-wrap',
      }}>
      {text}
    </pre>
  )
}
